import React, { useEffect, useState } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { BasecampLayout, AreaLayout } from '../../components/Layout';
import { PageHeader, Card } from '../../components/UI';
import TeamWorldMap from './TeamWorldMap';

// HB World — kultura i organizacija. Sadržaj prenet iz mockup-a (hbworld view).

const SUBNAV = [
  { href: '/hbworld/story', label: 'Our Story' },
  { href: '/hbworld/who', label: 'Who We Are' },
  { href: '/hbworld/code', label: 'Code of Honour' },
  { href: '/hbworld/whatwedo', label: 'What We Do' },
  { href: '/hbworld/oi', label: 'Open Innovation Culture' },
];

const pStyle = { fontSize: '0.95rem', lineHeight: 1.7, color: 'var(--ink-mid)', marginBottom: 14 };

function Bullets({ items }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10, marginTop: 16 }}>
      {items.map((t, i) => (
        <Card key={i} style={{ padding: '12px 16px', fontSize: '0.88rem', color: 'var(--ink-soft)' }}>▪ {t}</Card>
      ))}
    </div>
  );
}

const CONTENT = {
  story: {
    title: 'Our Story',
    body: (
      <>
        <p style={pStyle}>
          <b>Who am I and what am I doing here?</b> Imagine influencing hundreds, even thousands of individuals,
          creating lasting impact — people reaching out years later to tell you how your guidance shaped their
          lives. This is more than a job; it is a lifestyle.
        </p>
        <p style={pStyle}>
          <b>Our heroes.</b> December 14, 1911 — Roald Amundsen wins the race to the South Pole. But he wasn't
          alone: Ludvig Hansen and Andreas Beck, two forgotten ice pilots, steered the expedition safely through
          the ice. History remembers the man who planted the flag. We work for the ones who made it possible.
        </p>
      </>
    ),
  },
  who: {
    title: 'Who We Are',
    body: (
      <>
        <p style={pStyle}>
          The invisible compass: an international training company focused on the development of people —
          22 countries, 5 continents, roots since 1963, established as Hansen Beck in 2019. The organisation:
          Ice Pilots, Business Partners, Hansen Beck GmbH and its shareholders.
        </p>
        <Bullets items={['The network and territories', 'Governance in brief', 'Leadership']} />
      </>
    ),
  },
  code: {
    title: 'Code of Honour',
    body: (
      <>
        <p style={pStyle}>
          Each member shapes the organisation. Three core roles — Ice Pilots, HB Business Partners,
          HB Shareholders — with the behaviours we owe each other: in the training room, in front of customers,
          and inside the community.
        </p>
        <Bullets items={['The roles in detail', 'Behaviours we honour', 'How we resolve disagreements']} />
      </>
    ),
  },
  whatwedo: {
    title: 'What We Do — Unlocking Human Potential',
    body: (
      <p style={pStyle}>
        Success isn't solely about achieving results or mastering skills; it's fundamentally about influence.
        Typical training emphasises the what (results) and the how (skills); Hansen Beck distinguishes itself
        by the who — the influence participants' actions exert on the broader organisational context.
        Our programmes holistically develop Results, Mindset, Skills and Influence.
      </p>
    ),
  },
  oi: {
    title: 'Open Innovation Culture',
    body: (
      <>
        <p style={pStyle}>
          New content is developed by individuals on their own initiative, then shared with the community and
          integrated — the culture Basecamp digitalises through the Commons and the approval flow. Every
          contribution is HB property, credited to its creator.
        </p>
        <Bullets items={['How contributions work → Contribute', 'Custodians — who curates what', 'Recognition']} />
      </>
    ),
  },
};

// Live counter since Hansen Beck started (1 July 2019) — whole days only,
// styled as a clock: a small analog face + flip-clock digit tiles.
const HB_FOUNDED = new Date(2019, 6, 1, 0, 0, 0);

function AnalogFace({ now }) {
  const d = new Date(now);
  const s = d.getSeconds(), m = d.getMinutes() + s / 60, h = (d.getHours() % 12) + m / 60;
  const hand = (deg, len, w, color) => (
    <line x1="50" y1="50" x2="50" y2={50 - len} stroke={color} strokeWidth={w} strokeLinecap="round" transform={`rotate(${deg} 50 50)`} />
  );
  return (
    <svg viewBox="0 0 100 100" width="60" height="60" aria-hidden="true" style={{ flexShrink: 0 }}>
      <circle cx="50" cy="50" r="46" fill="rgba(255,255,255,0.04)" stroke="rgba(255,255,255,0.55)" strokeWidth="2.5" />
      {Array.from({ length: 12 }, (_, i) => (
        <line key={i} x1="50" y1="8" x2="50" y2={i % 3 === 0 ? 16 : 12} stroke="rgba(255,255,255,0.6)" strokeWidth={i % 3 === 0 ? 2.5 : 1.5} transform={`rotate(${i * 30} 50 50)`} />
      ))}
      {hand(h * 30, 24, 4, '#fff')}
      {hand(m * 6, 34, 3, '#fff')}
      {hand(s * 6, 38, 1.4, 'var(--accent, #c8102e)')}
      <circle cx="50" cy="50" r="3" fill="var(--accent, #c8102e)" />
    </svg>
  );
}

// One flip-clock tile per character (digits and the thousands comma), with the fold line across the middle.
function DigitTiles({ value, label }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
      <div style={{ display: 'flex', gap: 4, alignItems: 'flex-end' }}>
        {String(value).split('').map((ch, i) => ch === ',' ? (
          <span key={i} style={{ fontFamily: 'var(--font-display)', fontSize: '1.6rem', color: 'rgba(255,255,255,0.7)', lineHeight: 1, margin: '0 1px' }}>,</span>
        ) : (
          <span key={i} style={{
            position: 'relative', width: 28, height: 42, borderRadius: 5,
            background: 'linear-gradient(180deg, #2b2b2b 0%, #1d1d1d 50%, #151515 50%, #111 100%)',
            boxShadow: '0 3px 8px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.08)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontFamily: 'var(--font-display)', fontSize: '1.6rem', color: '#fff', fontVariantNumeric: 'tabular-nums',
          }}>
            {ch}
            <span style={{ position: 'absolute', left: 0, right: 0, top: '50%', height: 1, background: 'rgba(0,0,0,0.6)' }} />
          </span>
        ))}
      </div>
      {label && <div style={{ fontSize: '0.6rem', letterSpacing: '0.16em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.5)' }}>{label}</div>}
    </div>
  );
}

function SinceCounter() {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);
  // Whole calendar days since founding (local dates, so DST shifts don't skew the count).
  const d = new Date(now);
  const days = Math.max(0, Math.round((new Date(d.getFullYear(), d.getMonth(), d.getDate()) - HB_FOUNDED) / 86400000));
  const daysText = days.toLocaleString('en-US');
  // Compact badge sized to its content (not full width): clock | since-date over the day tiles.
  return (
    <Card style={{ width: 'fit-content', maxWidth: '100%', padding: '14px 22px 14px 16px', marginBottom: 22, background: 'var(--ink)', color: '#fff', display: 'flex', alignItems: 'center', gap: 16 }}>
      <AnalogFace now={now} />
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: '0.66rem', letterSpacing: '0.18em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.5)', marginBottom: 8 }}>
          Hansen Beck since <span style={{ color: 'rgba(255,255,255,0.85)' }}>1 July 2019</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10 }} aria-label={`${daysText} days`}>
          <DigitTiles value={daysText} />
          <span style={{ fontFamily: 'var(--font-display)', fontSize: '1.15rem', color: 'rgba(255,255,255,0.85)', paddingBottom: 4 }}>days</span>
        </div>
      </div>
    </Card>
  );
}

export default function HBWorld() {
  const { sub } = useParams();
  if (!sub) return <Navigate to="/hbworld/story" replace />;
  const content = CONTENT[sub] || CONTENT.story;

  return (
    <BasecampLayout>
      <PageHeader title="HB World — culture and organisation" subtitle="About Hansen Beck & why we are here" />
      <SinceCounter />
      <TeamWorldMap />
      <AreaLayout items={SUBNAV}>
        <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.3rem', marginBottom: 14 }}>{content.title}</h2>
        {content.body}
      </AreaLayout>
    </BasecampLayout>
  );
}
